/**
 * Workshop Memory — typed API client.
 *
 * All HTTP calls to the backend pass through this module.
 * The base URL is controlled by NEXT_PUBLIC_API_BASE_URL.
 *
 * Token strategy (MVP-0):
 *   The JWT access token is stored in localStorage under the key
 *   "wm_token".  localStorage is readable by JavaScript running on the
 *   same origin and is therefore vulnerable to XSS.  This trade-off is
 *   accepted for MVP-0.  A production hardening step would move the token
 *   to an HttpOnly cookie managed by the server.
 */

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000";

const TOKEN_KEY = "wm_token";

// ── Token helpers ─────────────────────────────────────────────────────────────

export function saveToken(token: string): void {
  if (typeof window !== "undefined") localStorage.setItem(TOKEN_KEY, token);
}

export function loadToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function clearToken(): void {
  if (typeof window !== "undefined") localStorage.removeItem(TOKEN_KEY);
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  ok: boolean;
  status: number;
}

export interface HealthResponse {
  status: string;
  database: string;
}

export interface VersionResponse {
  version: string;
  environment: string;
  build_commit: string | null;
  /** WM-098: ISO-8601 build timestamp, null if not set at build time. */
  build_timestamp: string | null;
}

/** WM-101: Combined operational status response from GET /status. */
export interface StatusResponse {
  status: string;
  database: string;
  build_commit: string | null;
  build_timestamp: string | null;
  environment: string;
  version: string;
  ai_failures_24h: number;
  upload_failures_24h: number;
  export_failures_24h: number;
  total_failures_24h: number;
  last_ai_analysis_at: string | null;
}

export interface UserPublic {
  id: number;
  email: string;
  name: string;
  /** Mirrors backend UserPublic.is_platform_admin (WM-090). */
  is_platform_admin: boolean;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export interface WorkshopPublic {
  id: number;
  name: string;
  owner_user_id: number;
  description: string | null;
  organization: string | null;
  locale: string;
  timezone: string;
  created_at: string;
  updated_at: string;
}

export interface WorkshopSettingsUpdate {
  name?: string;
  description?: string | null;
  organization?: string | null;
  locale?: string;
  timezone?: string;
}

// WM-081: Audit log
export interface AuditLogEntry {
  id: number;
  workshop_id: number;
  actor_user_id: number | null;
  event_type: string;
  entity_type: string | null;
  entity_id: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface ActivityResponse {
  total: number;
  limit: number;
  offset: number;
  entries: AuditLogEntry[];
  q?: string | null;
  event_type?: string | null;
}

// WM-078: User profile update
export interface UserProfileUpdate {
  name: string;
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

export type LocationType =
  | "SITE"
  | "ROOM"
  | "AREA"
  | "RACK"
  | "CABINET"
  | "SHELF"
  | "DRAWER"
  | "BOX"
  | "BIN"
  | "BAG"
  | "OTHER";

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  SITE: "Site",
  ROOM: "Room",
  AREA: "Area",
  RACK: "Rack",
  CABINET: "Cabinet",
  SHELF: "Shelf",
  DRAWER: "Drawer",
  BOX: "Box",
  BIN: "Bin",
  BAG: "Bag",
  OTHER: "Other",
};

export interface LocationBrief {
  id: number;
  code: string;
  name: string;
  location_type: LocationType;
}

export interface LocationPublic extends LocationBrief {
  workshop_id: number;
  parent_location_id: number | null;
  description: string | null;
  qr_token: string;
  created_at: string;
  updated_at: string;
}

export interface LocationPathSegment {
  id: number;
  code: string;
  name: string;
  location_type: LocationType;
}

export interface LocationWithPath extends LocationPublic {
  path: LocationPathSegment[];
}

/** WM-BL01 — minimal location data for bulk QR label generation. */
export interface LocationForLabel {
  id: number;
  code: string;
  name: string;
  location_type: LocationType;
  qr_token: string;
  parent_location_id: number | null;
}

export interface BulkLocationCreate {
  workshop_id: number;
  parent_location_id?: number | null;
  prefix: string;
  start: number;
  end: number;
  padding: number;
  location_type: LocationType;
  name_template: string;
}

export interface BulkLocationResponse {
  count: number;
  locations: LocationBrief[];
}

// ── AI Analysis Types (WM-028→034) ───────────────────────────────────────────

export type AnalysisStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED";

export type ObservationSource =
  | "AI"
  | "AI_CONFIRMED_BY_USER"
  | "USER";

export type ObservationStatus =
  | "DETECTED"
  | "CONFIRMED"
  | "REJECTED"
  | "UNKNOWN";

// WM-047→056 (additive) — minimal InventoryItem reference exposed on an
// Observation so the UI can render "In inventory: <name>" + [Open item]
// instead of "Add to inventory" without a second round-trip.
export interface LinkedInventoryItemBrief {
  id: number;
  canonical_name: string;
}

export interface ObservationPublic {
  id: number;
  analysis_id: number;
  photo_id: number;
  location_id: number;
  workshop_id: number;
  raw_ai_label: string;
  normalized_label: string;
  generic_name: string;
  category: string | null;
  brand: string | null;
  model: string | null;
  part_number: string | null;
  visible_text: string[];
  description: string | null;
  confidence: number;
  quantity_estimate: number | null;
  quantity_confidence: number | null;
  source: ObservationSource;
  status: ObservationStatus;
  user_defined_label: boolean;
  // null when this observation has not yet been linked to an InventoryItem.
  linked_inventory_item: LinkedInventoryItemBrief | null;
  created_at: string;
  updated_at: string;
}

export interface PhotoAnalysisPublic {
  id: number;
  photo_id: number;
  workshop_id: number;
  provider: string;
  model: string;
  prompt_version: string;
  status: AnalysisStatus;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
  observations: ObservationPublic[];
}

export interface AnalysisTriggerResponse {
  analysis_id: number;
  status: string;
}

export interface ObservationCorrect {
  normalized_label?: string | null;
  generic_name?: string | null;
  category?: string | null;
  brand?: string | null;
  model?: string | null;
  part_number?: string | null;
  description?: string | null;
}

// ── Search (WM-037→042) ───────────────────────────────────────────────────────

export interface SearchResultLocation {
  id: number;
  code: string;
  name: string;
  breadcrumb: LocationPathSegment[];
}

export interface SearchResultPhoto {
  id: number;
  thumbnail_available: boolean;
}

export interface SearchMatchInfo {
  score: number;
  reason: string;
}

export interface SearchResultItem {
  observation_id: number;
  display_label: string;
  generic_name: string;
  category: string | null;
  brand: string | null;
  model: string | null;
  part_number: string | null;
  observation_status: ObservationStatus;
  observation_source: ObservationSource;
  confidence: number;
  quantity_estimate: number | null;
  location: SearchResultLocation;
  photo: SearchResultPhoto;
  match: SearchMatchInfo;
}

export interface SearchResponse {
  query: string;
  count: number;
  limit: number;
  results: SearchResultItem[];
}

// ── Inventory Items (WM-047→056) ──────────────────────────────────────────
//
// Mirrors backend/app/schemas/inventory.py exactly. Observation = evidence-
// level statement ("AI saw something in this photo"). InventoryItem =
// operational workshop knowledge ("this location has this part"). These are
// different concepts — never collapsed into one another in this client.

export type QuantityMode = "UNKNOWN" | "ESTIMATED" | "EXACT";

export const QUANTITY_MODE_LABELS: Record<QuantityMode, string> = {
  UNKNOWN: "Quantity unknown",
  ESTIMATED: "Estimated",
  EXACT: "Exact count",
};

export type ItemStatus = "ACTIVE" | "ARCHIVED";

export interface InventoryItemCreate {
  canonical_name: string;
  category?: string | null;
  manufacturer?: string | null;
  brand?: string | null;
  model?: string | null;
  part_number?: string | null;
  quantity?: number | null;
  quantity_mode?: QuantityMode;
  notes?: string | null;
}

export interface InventoryItemUpdate {
  canonical_name?: string | null;
  category?: string | null;
  manufacturer?: string | null;
  brand?: string | null;
  model?: string | null;
  part_number?: string | null;
  quantity?: number | null;
  quantity_mode?: QuantityMode | null;
  notes?: string | null;
}

export interface InventoryItemFromObservationCreate {
  canonical_name?: string | null;
  category?: string | null;
  manufacturer?: string | null;
  brand?: string | null;
  model?: string | null;
  part_number?: string | null;
  quantity?: number | null;
  quantity_mode?: QuantityMode | null;
  notes?: string | null;
}

export interface InventoryItemAliasPublic {
  id: number;
  alias: string;
  created_at: string;
}

export interface EvidenceObservationSummary {
  observation_id: number;
  photo_id: number;
  display_label: string;
  confidence: number;
  observation_status: ObservationStatus;
  observation_source: ObservationSource;
  created_at: string;
}

export interface InventoryItemLocationContext {
  id: number;
  code: string;
  name: string;
  breadcrumb: LocationPathSegment[];
}

export interface InventoryItemPublic {
  id: number;
  workshop_id: number;
  location_id: number;
  canonical_name: string;
  category: string | null;
  manufacturer: string | null;
  brand: string | null;
  model: string | null;
  part_number: string | null;
  quantity: number | null;
  quantity_mode: QuantityMode;
  notes: string | null;
  status: ItemStatus;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryItemDetail extends InventoryItemPublic {
  location: InventoryItemLocationContext;
  aliases: InventoryItemAliasPublic[];
  evidence: EvidenceObservationSummary[];
}

export interface InventoryItemAliasCreate {
  alias: string;
}

export interface InventoryCandidate {
  item: InventoryItemPublic;
  location: InventoryItemLocationContext;
  reason: string;
  same_location: boolean;
}

export interface InventorySuggestionsResponse {
  observation_id: number;
  candidates: InventoryCandidate[];
}

export interface LinkObservationResult {
  item: InventoryItemDetail;
  evidence_created: boolean;
}

// ── Dual-source search (WM-055) — "Find a part" ────────────────────────────
//
// One relevance-ranked list mixing InventoryItem results and un-inventoried
// Observation results, discriminated by `source`. An Observation already
// linked to an InventoryItem is represented ONLY via the Item — never
// duplicated as an "AI-only" result.

export interface InventorySearchResultItem {
  source: "inventory_item";
  item_id: number;
  canonical_name: string;
  category: string | null;
  manufacturer: string | null;
  brand: string | null;
  model: string | null;
  part_number: string | null;
  quantity: number | null;
  quantity_mode: QuantityMode;
  item_status: ItemStatus;
  location: SearchResultLocation;
  match: SearchMatchInfo;
  // null when this Item has no photographic evidence (manually created).
  photo: SearchResultPhoto | null;
}

export interface InventorySearchResultObservation {
  source: "observation";
  observation_id: number;
  display_label: string;
  generic_name: string;
  category: string | null;
  brand: string | null;
  model: string | null;
  part_number: string | null;
  observation_status: ObservationStatus;
  observation_source: ObservationSource;
  confidence: number;
  quantity_estimate: number | null;
  location: SearchResultLocation;
  photo: SearchResultPhoto;
  match: SearchMatchInfo;
}

export type InventorySearchResult =
  | InventorySearchResultItem
  | InventorySearchResultObservation;

export interface InventorySearchResponse {
  query: string;
  count: number;
  limit: number;
  results: InventorySearchResult[];
}

export interface PhotoPublic {
  id: number;
  location_id: number;
  workshop_id: number;
  original_filename: string;
  mime_type: string;
  file_size: number;
  width: number;
  height: number;
  sha256: string;
  source: string | null;
  captured_at: string | null;
  created_at: string;
  created_by_user_id: number | null;
  url: string;
  thumbnail_url: string | null;
}

// ── Movement & Quantity (WM-067→076) ─────────────────────────────────────────
//
// These types mirror backend/app/schemas/movement.py exactly.

export interface LocationContext {
  id: number;
  code: string;
  name: string;
  breadcrumb: LocationPathSegment[];
}

export interface MoveItemRequest {
  destination_location_id: number;
  note?: string | null;
}

export interface MovementPublic {
  id: number;
  inventory_item_id: number;
  from_location: LocationContext;
  to_location: LocationContext;
  moved_by_user_id: number | null;
  moved_at: string;
  note: string | null;
}

export interface SetQuantityRequest {
  quantity: number | null;
  quantity_mode: QuantityMode;
  reason?: string | null;
}

export interface AdjustQuantityRequest {
  delta: number;
  reason?: string | null;
}

export interface QuantityEventPublic {
  id: number;
  inventory_item_id: number;
  old_quantity: number | null;
  old_quantity_mode: string;
  new_quantity: number | null;
  new_quantity_mode: string;
  changed_by_user_id: number | null;
  changed_at: string;
  reason: string | null;
  change_type: string;
}

/** Extended detail — supersedes InventoryItemDetail for GET /api/inventory-items/{id} */
export interface InventoryItemDetailExtended extends InventoryItemPublic {
  location: InventoryItemLocationContext;
  aliases: InventoryItemAliasPublic[];
  evidence: EvidenceObservationSummary[];
  // WM-071: operational timestamps
  last_seen_at: string | null;
  last_verified_at: string | null;
  last_moved_at: string | null;
  last_quantity_changed_at: string | null;
  // WM-068/070: recent history (newest first, up to 10 of each)
  recent_movements: MovementPublic[];
  recent_quantity_events: QuantityEventPublic[];
}

export interface LocationPickerResult {
  id: number;
  code: string;
  name: string;
  breadcrumb: LocationPathSegment[];
}

// ── Low-level fetch ───────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  options?: RequestInit,
  withAuth = false
): Promise<ApiResponse<T>> {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options?.headers as Record<string, string> ?? {}),
    };
    if (withAuth) {
      const token = loadToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorMsg = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        errorMsg = body?.detail ?? errorMsg;
      } catch {
        // ignore parse failure — error responses may not contain JSON
      }
      return { data: null, error: errorMsg, ok: false, status: response.status };
    }

    // Robust body parser: do NOT call .json() when there is no body.
    //
    // HTTP 204 No Content carries no body by specification.
    // A Content-Length of "0" means the server explicitly declared an empty
    // body.  A missing or non-JSON Content-Type means the payload is not JSON.
    //
    // Calling response.json() on an empty body throws:
    //   Chrome/Edge: "Unexpected end of JSON input"
    //   Safari/WebKit: "The string did not match the expected pattern"
    // — exactly the two errors observed in staging.
    //
    // Safe rule: only parse JSON when a JSON body is actually present.
    const contentLength = response.headers.get("content-length");
    const contentType = response.headers.get("content-type") ?? "";
    const hasBody =
      response.status !== 204 &&
      contentLength !== "0" &&
      contentType.includes("application/json");

    const data: T = hasBody ? await response.json() : (null as unknown as T);
    return { data, error: null, ok: true, status: response.status };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Network error";
    return { data: null, error: message, ok: false, status: 0 };
  }
}

/**
 * Multipart/form-data upload — no Content-Type header so browser sets
 * the correct boundary automatically.
 */
async function uploadFile<T>(
  path: string,
  formData: FormData,
): Promise<ApiResponse<T>> {
  try {
    const token = loadToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      let errorMsg = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        errorMsg = body?.detail ?? errorMsg;
      } catch {
        // ignore parse failure — error responses may not contain JSON
      }
      return { data: null, error: errorMsg, ok: false, status: response.status };
    }

    // Same robust body parser as request() — see comment there.
    const uploadContentLength = response.headers.get("content-length");
    const uploadContentType = response.headers.get("content-type") ?? "";
    const uploadHasBody =
      response.status !== 204 &&
      uploadContentLength !== "0" &&
      uploadContentType.includes("application/json");

    const data: T = uploadHasBody ? await response.json() : (null as unknown as T);
    return { data, error: null, ok: true, status: response.status };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Network error";
    return { data: null, error: message, ok: false, status: 0 };
  }
}

// ── WM-060: Computed inventory status ────────────────────────────────────────

export type LocationInventoryStatus =
  | "NOT_STARTED"
  | "NO_ANALYSIS"
  | "AI_PROCESSING"
  | "NEEDS_REVIEW"
  | "REVIEWED"
  | "AI_FAILED"
  | "STALE";

export const INVENTORY_STATUS_LABELS: Record<LocationInventoryStatus, string> = {
  NOT_STARTED: "Not inventoried",
  NO_ANALYSIS: "Waiting for AI analysis",
  AI_PROCESSING: "AI analysis in progress",
  NEEDS_REVIEW: "Needs review",
  REVIEWED: "Reviewed",
  AI_FAILED: "AI analysis failed",
  STALE: "Update needed",
};

export const INVENTORY_STATUS_COLORS: Record<LocationInventoryStatus, string> = {
  NOT_STARTED: "bg-gray-100 text-gray-600",
  NO_ANALYSIS: "bg-blue-50 text-blue-600",
  AI_PROCESSING: "bg-purple-50 text-purple-600",
  NEEDS_REVIEW: "bg-yellow-50 text-yellow-700",
  REVIEWED: "bg-green-100 text-green-700",
  AI_FAILED: "bg-red-50 text-red-600",
  STALE: "bg-orange-50 text-orange-600",
};

// WM-061: Location summary
export interface LocationSummaryPublic {
  location_id: number;
  computed_status: LocationInventoryStatus;
  photo_count: number;
  relevant_observation_count: number;
  confirmed_count: number;
  corrected_or_renamed_count: number;
  rejected_count: number;
  unknown_count: number;
  needs_review_count: number;
  inventory_item_count: number;
  last_photo_at: string | null;
  last_ai_analysis_at: string | null;
  last_reviewed_at: string | null;
  last_activity_at: string | null;
}

// WM-062: Child summary (batch endpoint)
export interface ChildSummaryPublic {
  location_id: number;
  code: string;
  name: string;
  location_type: LocationType;
  computed_status: LocationInventoryStatus;
  photo_count: number;
  relevant_observation_count: number;
  confirmed_count: number;
  needs_review_count: number;
  rejected_count: number;
  inventory_item_count: number;
  last_activity_at: string | null;
  child_count: number;
}

// WM-063: Location aggregate coverage
export interface LocationAggregatePublic {
  location_id: number;
  total_leaf_locations: number;
  reviewed_leaf_locations: number;
  needs_review_leaf_locations: number;
  not_started_leaf_locations: number;
  stale_leaf_locations: number;
  failed_leaf_locations: number;
  other_state_leaf_locations: number;
  coverage_percent: number | null;
  descendant_inventory_item_count: number;
}

// WM-064: Workshop coverage
export interface TopLevelCoverageEntry {
  location_id: number;
  code: string;
  name: string;
  location_type: LocationType;
  total_leaf_locations: number;
  reviewed_leaf_locations: number;
  coverage_percent: number | null;
  descendant_inventory_item_count: number;
}

export interface WorkshopCoveragePublic {
  workshop_id: number;
  total_leaf_locations: number;
  reviewed_leaf_locations: number;
  needs_review_leaf_locations: number;
  not_started_leaf_locations: number;
  stale_leaf_locations: number;
  failed_leaf_locations: number;
  other_state_leaf_locations: number;
  coverage_percent: number | null;
  total_inventory_items: number;
  top_level_locations: TopLevelCoverageEntry[];
}

// ── API surface ───────────────────────────────────────────────────────────────

export const api = {
  // ── Health ────────────────────────────────────────────────────────────────
  health: () => request<HealthResponse>("/health"),
  version: () => request<VersionResponse>("/version"),

  // ── Auth ──────────────────────────────────────────────────────────────────
  register: (email: string, password: string, name: string) =>
    request<UserPublic>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, name }),
    }),

  login: (email: string, password: string) =>
    request<TokenResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<UserPublic>("/api/auth/me", {}, true),

  // ── Workshops ─────────────────────────────────────────────────────────────
  createWorkshop: (name: string) =>
    request<WorkshopPublic>("/api/workshops", {
      method: "POST",
      body: JSON.stringify({ name }),
    }, true),

  listWorkshops: () =>
    request<WorkshopPublic[]>("/api/workshops", {}, true),

  getWorkshop: (id: number) =>
    request<WorkshopPublic>(`/api/workshops/${id}`, {}, true),

  renameWorkshop: (id: number, name: string) =>
    request<WorkshopPublic>(`/api/workshops/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }, true),

  // WM-077: Workshop Settings
  updateWorkshopSettings: (id: number, payload: WorkshopSettingsUpdate) =>
    request<WorkshopPublic>(`/api/workshops/${id}/settings`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }, true),

  // WM-079: Workshop Export — returns a Blob (CSV file)
  exportWorkshopInventory: async (workshopId: number, includeArchived = false): Promise<ApiResponse<Blob>> => {
    const qs = includeArchived ? "?include_archived=true" : "";
    const token = loadToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/workshops/${workshopId}/export${qs}`,
        { headers }
      );
      if (!response.ok) {
        let errorMsg = `HTTP ${response.status}`;
        try { const b = await response.json(); errorMsg = b?.detail ?? errorMsg; } catch { /* ignore */ }
        return { data: null, error: errorMsg, ok: false, status: response.status };
      }
      const blob = await response.blob();
      return { data: blob, error: null, ok: true, status: response.status };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error";
      return { data: null, error: message, ok: false, status: 0 };
    }
  },

  // WM-081: Workshop Activity (with server-side filters — WM-086)
  getWorkshopActivity: (
    workshopId: number,
    params?: {
      limit?: number;
      offset?: number;
      q?: string;
      event_type?: string;
      date_from?: string;
      date_to?: string;
    }
  ) => {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    if (params?.offset !== undefined) qs.set("offset", String(params.offset));
    if (params?.q) qs.set("q", params.q);
    if (params?.event_type) qs.set("event_type", params.event_type);
    if (params?.date_from) qs.set("date_from", params.date_from);
    if (params?.date_to) qs.set("date_to", params.date_to);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<ActivityResponse>(`/api/workshops/${workshopId}/activity${suffix}`, {}, true);
  },

  // WM-078: User Profile
  updateUserProfile: (payload: UserProfileUpdate) =>
    request<UserPublic>("/api/users/me", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }, true),

  changePassword: (payload: PasswordChangeRequest) =>
    request<null>("/api/users/me/password", {
      method: "POST",
      body: JSON.stringify(payload),
    }, true),

  // WM-064: Workshop inventory coverage page
  getWorkshopCoverage: (workshopId: number) =>
    request<WorkshopCoveragePublic>(`/api/workshops/${workshopId}/coverage`, {}, true),

  // ── Locations ─────────────────────────────────────────────────────────────
  listRootLocations: (workshopId: number) =>
    request<LocationBrief[]>(`/api/workshops/${workshopId}/locations`, {}, true),

  createLocation: (payload: {
    workshop_id: number;
    parent_location_id?: number | null;
    code: string;
    name: string;
    location_type: LocationType;
    description?: string | null;
  }) =>
    request<LocationPublic>("/api/locations", {
      method: "POST",
      body: JSON.stringify(payload),
    }, true),

  getLocation: (id: number) =>
    request<LocationWithPath>(`/api/locations/${id}`, {}, true),

  getChildren: (locationId: number) =>
    request<LocationBrief[]>(`/api/locations/${locationId}/children`, {}, true),

  // WM-BL01: All descendants for bulk label printing
  getDescendants: (locationId: number) =>
    request<LocationForLabel[]>(`/api/locations/${locationId}/descendants`, {}, true),

  // WM-061: Location summary
  getLocationSummary: (locationId: number) =>
    request<LocationSummaryPublic>(`/api/locations/${locationId}/summary`, {}, true),

  // WM-062: Children with inventory status (batch, no N+1)
  getChildrenSummaries: (locationId: number) =>
    request<ChildSummaryPublic[]>(`/api/locations/${locationId}/children-summaries`, {}, true),

  // WM-063: Location aggregate coverage
  getLocationAggregate: (locationId: number) =>
    request<LocationAggregatePublic>(`/api/locations/${locationId}/aggregate`, {}, true),

  // WM-058: Update location type via existing PATCH
  updateLocationType: (id: number, location_type: LocationType) =>
    request<LocationPublic>(`/api/locations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ location_type }),
    }, true),

  renameLocation: (id: number, name: string) =>
    request<LocationPublic>(`/api/locations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }, true),

  bulkCreate: (payload: BulkLocationCreate) =>
    request<BulkLocationResponse>("/api/locations/bulk", {
      method: "POST",
      body: JSON.stringify(payload),
    }, true),

  // B1: Safe location delete — returns 204 on success, 409 on non-empty
  deleteLocation: (locationId: number) =>
    request<null>(`/api/locations/${locationId}`, { method: "DELETE" }, true),

  // ── Photos (WM-019/020/021) ───────────────────────────────────────────────
  uploadPhoto: (locationId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return uploadFile<PhotoPublic>(`/api/locations/${locationId}/photos`, form);
  },

  listPhotos: (locationId: number) =>
    request<PhotoPublic[]>(`/api/locations/${locationId}/photos`, {}, true),

  deletePhoto: (photoId: number) =>
    request<null>(`/api/photos/${photoId}`, { method: "DELETE" }, true),

  /**
   * Fetch photo binary data (thumbnail or original) with Bearer authentication.
   *
   * Browser <img src> never sends Authorization headers, so photo binary
   * endpoints cannot be used directly as image src values.  This method
   * performs an authenticated fetch and returns the response Blob.
   *
   * The caller is responsible for:
   *   - creating a browser Object URL:  URL.createObjectURL(blob)
   *   - revoking it when done:          URL.revokeObjectURL(objectUrl)
   *
   * @param photoId - Photo record ID
   * @param kind    - "thumbnail" → /thumbnail endpoint; "original" → /file endpoint
   */
  fetchPhotoBinary: async (
    photoId: number,
    kind: "thumbnail" | "original"
  ): Promise<ApiResponse<Blob>> => {
    const path =
      kind === "thumbnail"
        ? `/api/photos/${photoId}/thumbnail`
        : `/api/photos/${photoId}/file`;
    try {
      const token = loadToken();
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE_URL}${path}`, { headers });

      if (!response.ok) {
        let errorMsg = `HTTP ${response.status}`;
        try {
          const body = await response.json();
          errorMsg = body?.detail ?? errorMsg;
        } catch {
          // ignore — binary endpoint may not return JSON on error
        }
        return { data: null, error: errorMsg, ok: false, status: response.status };
      }

      const blob = await response.blob();
      return { data: blob, error: null, ok: true, status: response.status };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error";
      return { data: null, error: message, ok: false, status: 0 };
    }
  },

  // ── Search (WM-037→042) — "Find my part" ─────────────────────────────────
  search: (
    workshopId: number,
    q: string,
    params?: { limit?: number; status?: string; category?: string }
  ) => {
    const qs = new URLSearchParams({ q });
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    if (params?.status) qs.set("status", params.status);
    if (params?.category) qs.set("category", params.category);
    return request<SearchResponse>(
      `/api/workshops/${workshopId}/search?${qs.toString()}`,
      {},
      true
    );
  },

  // ── QR resolution (WM-022) ────────────────────────────────────────────────
  resolveQr: (qrToken: string) =>
    request<LocationBrief>(`/api/qr/${qrToken}`, {}, true),

  // ── AI Analysis (WM-031→036) ──────────────────────────────────────────────
  triggerAnalysis: (photoId: number) =>
    request<AnalysisTriggerResponse>(`/api/photos/${photoId}/analyze`, {
      method: "POST",
    }, true),

  getPhotoAnalyses: (photoId: number) =>
    request<PhotoAnalysisPublic[]>(`/api/photos/${photoId}/analyses`, {}, true),

  getAnalysis: (analysisId: number) =>
    request<PhotoAnalysisPublic>(`/api/analyses/${analysisId}`, {}, true),

  confirmObservation: (observationId: number) =>
    request<ObservationPublic>(`/api/observations/${observationId}/confirm`, {
      method: "POST",
    }, true),

  rejectObservation: (observationId: number) =>
    request<ObservationPublic>(`/api/observations/${observationId}/reject`, {
      method: "POST",
    }, true),

  correctObservation: (observationId: number, payload: ObservationCorrect) =>
    request<ObservationPublic>(`/api/observations/${observationId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }, true),

  // ── Inventory Items (WM-047→056) ─────────────────────────────────────────

  createInventoryItem: (locationId: number, payload: InventoryItemCreate) =>
    request<InventoryItemPublic>(
      `/api/locations/${locationId}/inventory-items`,
      { method: "POST", body: JSON.stringify(payload) },
      true
    ),

  listInventoryItems: (locationId: number, includeArchived = false) => {
    const qs = includeArchived ? "?include_archived=true" : "";
    return request<InventoryItemPublic[]>(
      `/api/locations/${locationId}/inventory-items${qs}`,
      {},
      true
    );
  },

  getInventoryItem: (itemId: number) =>
    request<InventoryItemDetailExtended>(`/api/inventory-items/${itemId}`, {}, true),

  updateInventoryItem: (itemId: number, payload: InventoryItemUpdate) =>
    request<InventoryItemPublic>(
      `/api/inventory-items/${itemId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      true
    ),

  archiveInventoryItem: (itemId: number) =>
    request<InventoryItemPublic>(
      `/api/inventory-items/${itemId}/archive`,
      { method: "POST" },
      true
    ),

  unarchiveInventoryItem: (itemId: number) =>
    request<InventoryItemPublic>(
      `/api/inventory-items/${itemId}/unarchive`,
      { method: "POST" },
      true
    ),

  addInventoryItemAlias: (itemId: number, alias: string) =>
    request<InventoryItemAliasPublic>(
      `/api/inventory-items/${itemId}/aliases`,
      { method: "POST", body: JSON.stringify({ alias }) },
      true
    ),

  deleteInventoryItemAlias: (itemId: number, aliasId: number) =>
    request<null>(
      `/api/inventory-items/${itemId}/aliases/${aliasId}`,
      { method: "DELETE" },
      true
    ),

  // ── AI → Inventory reconciliation (WM-049/052) ───────────────────────────

  getInventorySuggestions: (observationId: number) =>
    request<InventorySuggestionsResponse>(
      `/api/observations/${observationId}/inventory-suggestions`,
      { method: "POST" },
      true
    ),

  createItemFromObservation: (
    observationId: number,
    overrides: InventoryItemFromObservationCreate = {}
  ) =>
    request<InventoryItemDetail>(
      `/api/observations/${observationId}/create-inventory-item`,
      { method: "POST", body: JSON.stringify(overrides) },
      true
    ),

  linkObservationToItem: (observationId: number, itemId: number) =>
    request<LinkObservationResult>(
      `/api/observations/${observationId}/link-inventory-item/${itemId}`,
      { method: "POST" },
      true
    ),

  // ── Dual-source search (WM-055) — "Find a part" ──────────────────────────

  inventorySearch: (
    workshopId: number,
    q: string,
    params?: { limit?: number; category?: string }
  ) => {
    const qs = new URLSearchParams({ q });
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    if (params?.category) qs.set("category", params.category);
    return request<InventorySearchResponse>(
      `/api/workshops/${workshopId}/inventory-search?${qs.toString()}`,
      {},
      true
    );
  },

  // ── Movement (WM-067/068) ─────────────────────────────────────────────────

  moveItem: (itemId: number, payload: MoveItemRequest) =>
    request<MovementPublic>(
      `/api/inventory-items/${itemId}/move`,
      { method: "POST", body: JSON.stringify(payload) },
      true
    ),

  getMovements: (itemId: number, limit = 10) =>
    request<MovementPublic[]>(
      `/api/inventory-items/${itemId}/movements?limit=${limit}`,
      {},
      true
    ),

  // ── Quantity (WM-069/070) ─────────────────────────────────────────────────

  setQuantity: (itemId: number, payload: SetQuantityRequest) =>
    request<QuantityEventPublic>(
      `/api/inventory-items/${itemId}/quantity/set`,
      { method: "POST", body: JSON.stringify(payload) },
      true
    ),

  adjustQuantity: (itemId: number, payload: AdjustQuantityRequest) =>
    request<QuantityEventPublic>(
      `/api/inventory-items/${itemId}/quantity/adjust`,
      { method: "POST", body: JSON.stringify(payload) },
      true
    ),

  getQuantityHistory: (itemId: number, limit = 10) =>
    request<QuantityEventPublic[]>(
      `/api/inventory-items/${itemId}/quantity-history?limit=${limit}`,
      {},
      true
    ),

  // ── Location picker (WM-073) ──────────────────────────────────────────────

  searchLocations: (
    workshopId: number,
    q: string,
    params?: { exclude?: number; limit?: number }
  ) => {
    const qs = new URLSearchParams({ q });
    if (params?.exclude !== undefined) qs.set("exclude", String(params.exclude));
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    return request<LocationPickerResult[]>(
      `/api/workshops/${workshopId}/locations/search?${qs.toString()}`,
      {},
      true
    );
  },
};

// ── Members & Invitations (WM-087/088) ────────────────────────────────────────

export type WorkshopRole = "OWNER" | "ADMIN" | "MEMBER" | "VIEWER";

/**
 * Backend contract (backend/app/schemas/member.py — MemberPublic):
 *   id, user_id, name, email, role, created_at
 *
 * Field names below MUST match the backend JSON response exactly.
 * The previous interface used user_name/user_email/joined_at — all wrong.
 * Root cause of Staging Bug #4: member identity was not rendered (both fields
 * evaluated to undefined), so every row other than "you" showed no identity.
 *
 * Authoritative backend source:
 *   backend/app/schemas/member.py  → name: str, email: str, created_at
 *   backend/app/services/member_service.py → list_members() JOIN returns name/email
 *   backend/app/api/members.py (lines 58–69) → MemberPublic(name=r["name"], ...)
 *
 * Compile-time guards in frontend/lib/__api_contract_test__.ts enforce this.
 */
export interface MemberPublic {
  id: number;
  user_id: number;
  workshop_id: number;
  /** User's display name from User.name column. Optional — may be empty string. */
  name: string;
  /** User's email address — always present. */
  email: string;
  role: WorkshopRole;
  /** ISO timestamp — backend field is created_at (membership creation, not user creation). */
  created_at: string;
}

export interface MemberRoleUpdate {
  role: WorkshopRole;
}

export interface MyRoleResponse {
  role: WorkshopRole;
  workshop_id: number;
  user_id: number;
}

export interface InvitationPublic {
  id: number;
  workshop_id: number;
  /** Backend field name is `email_normalized` (lowercase, trimmed). */
  email_normalized: string;
  role: WorkshopRole;
  expires_at: string;
  status: string;
  accepted_at: string | null;
  created_at: string;
}

export interface InvitationCreated extends InvitationPublic {
  raw_token: string;
  invite_url_path: string;
  /** WM-097: true if invitation email was delivered automatically. */
  email_sent: boolean;
  /** WM-097: safe error description if email_sent=false (never a secret). */
  email_error?: string | null;
}

export interface InvitationCreate {
  email: string;
  role: WorkshopRole;
}

export interface InvitationAccept {
  /** The raw token from the invitation URL (?token=...). */
  token: string;
}

/**
 * Safe read-only preview of an invitation.
 * Returned by GET /api/invitations/preview?token=... without authentication.
 * Does NOT include token_hash, invitation id, or raw token.
 */
export interface InvitationPreview {
  workshop_name: string;
  email_normalized: string;
  role: WorkshopRole;
  expires_at: string;
  /** InvitationStatus: PENDING | ACCEPTED | EXPIRED | REVOKED */
  status: string;
}

// ── Feedback (WM-091) ────────────────────────────────────────────────────────

export type FeedbackCategory =
  | "BUG"
  | "CONFUSING"
  | "FEATURE_REQUEST"
  | "OTHER";

export interface FeedbackCreate {
  category: FeedbackCategory;
  message: string;
  page_context?: string;
  workshop_id?: number;
}

export interface FeedbackPublic {
  id: number;
  category: FeedbackCategory;
  message: string;
  page_context: string | null;
  workshop_id: number | null;
  status: string;
  created_at: string;
}

// ── Admin / Pilot Dashboard (WM-090/093) ─────────────────────────────────────

export interface FunnelStep {
  step: number;
  label: string;
  count: number;
}

export interface PilotMetrics {
  total_users: number;
  active_users_7d: number;
  total_workshops: number;
  active_workshops_7d: number;
  locations_created: number;
  photos_uploaded: number;
  analyses_completed: number;
  /** P14: ACTIVE items only (excludes ARCHIVED) */
  items_created: number;
  searches_performed: number;
  items_moved: number;
  feedback_submitted: number;
  /** P14: global observation metrics */
  observations_count: number;
  confirmed_observations_count: number;
  /** P14: global AI cost totals from canonical ai_usage_events (formatted strings, 4dp) */
  estimated_cost_eur_total: string;
  actual_cost_eur_total: string;
  failures_24h: number;
  failures_7d: number;
  ai_failures_7d: number;
}

export interface WorkshopPilotSummary {
  id: number;
  name: string;
  owner_email: string;
  owner_name: string;
  member_count: number;
  location_count: number;
  item_count: number;
  photo_count: number;
  analysis_count: number;
  last_activity_at: string | null;
  is_pilot_disabled: boolean;
  created_at: string;
  /** P14: per-workshop AI cost estimated, all-time (formatted string, 4dp) */
  ai_cost_eur_estimated: string;
}

export interface PilotAdminResponse {
  metrics: PilotMetrics;
  funnel: FunnelStep[];
  workshops: WorkshopPilotSummary[];
}

/**
 * Paginated response from GET /api/admin/feedback.
 * Shape: { total, limit, offset, entries: FeedbackPublic[] }
 */
export interface AdminFeedbackResponse {
  total: number;
  limit: number;
  offset: number;
  entries: FeedbackPublic[];
}

export interface WorkshopToggleResponse {
  id: number;
  is_pilot_disabled: boolean;
}

// ── A6: AI Budget admin types ─────────────────────────────────────────────────

export interface WorkshopAIBudgetInfo {
  workshop_id: number;
  ai_daily_budget_eur: string;
  ai_hard_daily_ceiling_eur: string;
  used_estimated_eur_today: string;
  used_actual_eur_today: string | null;
  actual_cost_available: boolean;
  analyses_today: number;
  avg_cost_per_analysis_eur: string;
  resets_at: string;
  reset_timezone: string;
  reset_note: string;
}

// ── P3: Workshop-scoped AI Budget types (OWNER/ADMIN facing) ─────────────────

export interface WorkshopBudgetResponse {
  workshop_id: number;
  /** Formatted "1.00" (2dp) */
  daily_budget_eur: string;
  /** Formatted "5.00" — read-only for OWNER/ADMIN */
  hard_ceiling_eur: string;
  /** Total estimated cost today (4dp) */
  used_estimated_eur_today: string;
  /** Total actual cost today if available (4dp), null otherwise */
  used_actual_eur_today: string | null;
  /** False when no rows have actual_cost_eur */
  actual_cost_available: boolean;
  analyses_today: number;
  /** "0.0000" when analyses_today === 0 */
  avg_cost_per_analysis_eur: string;
  /** ISO-8601 UTC next midnight */
  resets_at: string;
  reset_timezone: string;
  reset_note: string;
}

// ── B1: Location delete blocker detail ────────────────────────────────────────

export interface LocationDeleteBlocker {
  message: string;
  children: number;
  photos: number;
  inventory_items: number;
}

// ── A7: Budget exceeded structured detail ─────────────────────────────────────

export interface BudgetExceededDetail {
  message: string;
  used_eur: string;
  budget_eur: string;
  resets_at: string;
}

// ── Frontend error telemetry ──────────────────────────────────────────────────

export interface FrontendErrorReport {
  message: string;
  context?: string;
}

// ── New API methods (P9 — extend api.ts only) ────────────────────────────────

Object.assign(api, {
  // Members
  listMembers: (workshopId: number) =>
    request<MemberPublic[]>(`/api/workshops/${workshopId}/members`, {}, true),

  getMyRole: (workshopId: number) =>
    request<MyRoleResponse>(`/api/workshops/${workshopId}/members/me`, {}, true),

  updateMemberRole: (workshopId: number, memberId: number, payload: MemberRoleUpdate) =>
    request<MemberPublic>(
      `/api/workshops/${workshopId}/members/${memberId}`,
      { method: "PATCH", body: JSON.stringify(payload) },
      true
    ),

  removeMember: (workshopId: number, memberId: number) =>
    request<void>(
      `/api/workshops/${workshopId}/members/${memberId}`,
      { method: "DELETE" },
      true
    ),

  // Invitations
  // POST /api/workshops/{id}/members/invite  — creates invitation (OWNER/ADMIN)
  createInvitation: (workshopId: number, payload: InvitationCreate) =>
    request<InvitationCreated>(
      `/api/workshops/${workshopId}/members/invite`,
      { method: "POST", body: JSON.stringify(payload) },
      true
    ),

  listInvitations: (workshopId: number) =>
    request<InvitationPublic[]>(
      `/api/workshops/${workshopId}/invitations`,
      {},
      true
    ),

  revokeInvitation: (workshopId: number, invitationId: number) =>
    request<void>(
      `/api/workshops/${workshopId}/invitations/${invitationId}`,
      { method: "DELETE" },
      true
    ),

  // Accept invitation
  acceptInvitation: (payload: InvitationAccept) =>
    request<MemberPublic>(
      `/api/invitations/accept`,
      { method: "POST", body: JSON.stringify(payload) },
      true
    ),

  // Preview invitation (unauthenticated — invitation landing page for logged-out users)
  // GET /api/invitations/preview?token=...
  // Returns workshop_name, email_normalized, role, expires_at, status.
  // Does NOT return token_hash or raw_token.
  previewInvitation: (token: string) =>
    request<InvitationPreview>(
      `/api/invitations/preview?token=${encodeURIComponent(token)}`,
      { method: "GET" },
      false  // no auth required
    ),

  // Feedback
  submitFeedback: (payload: FeedbackCreate) =>
    request<FeedbackPublic>(
      `/api/feedback`,
      { method: "POST", body: JSON.stringify(payload) },
      true
    ),

  // Admin / pilot
  getPilotAdmin: () =>
    request<PilotAdminResponse>(`/api/admin/pilot`, {}, true),

  toggleWorkshopDisabled: (workshopId: number) =>
    request<WorkshopToggleResponse>(
      `/api/admin/workshops/${workshopId}/toggle-disabled`,
      { method: "PATCH" },
      true
    ),

  // A6: AI budget admin endpoints (platform admin only — /api/admin/...)
  getWorkshopAIBudget: (workshopId: number) =>
    request<WorkshopAIBudgetInfo>(`/api/admin/workshops/${workshopId}/ai-budget`, {}, true),

  setWorkshopAIBudget: (workshopId: number, budgetEur: number) =>
    request<WorkshopAIBudgetInfo>(
      `/api/admin/workshops/${workshopId}/ai-budget`,
      { method: "PATCH", body: JSON.stringify({ ai_daily_budget_eur: budgetEur }) },
      true
    ),

  // P3: Workshop-scoped AI budget endpoints (OWNER/ADMIN — /api/workshops/...)
  getWorkshopBudget: (workshopId: number) =>
    request<WorkshopBudgetResponse>(`/api/workshops/${workshopId}/ai-budget`, {}, true),

  setWorkshopBudget: (workshopId: number, dailyBudgetEur: number) =>
    request<WorkshopBudgetResponse>(
      `/api/workshops/${workshopId}/ai-budget`,
      { method: "PATCH", body: JSON.stringify({ daily_budget_eur: dailyBudgetEur }) },
      true
    ),

  listAdminFeedback: () =>
    request<AdminFeedbackResponse>(`/api/admin/feedback`, {}, true),

  updateFeedbackStatus: (feedbackId: number, newStatus: string) =>
    request<FeedbackPublic>(
      `/api/admin/feedback/${feedbackId}`,
      { method: "PATCH", body: JSON.stringify({ status: newStatus }) },
      true
    ),

  // Analytics
  trackSearchResultOpened: (workshopId: number) =>
    request<void>(
      `/api/workshops/${workshopId}/search-result-opened`,
      { method: "POST" },
      true
    ),

  // Frontend error telemetry
  reportFrontendError: (payload: FrontendErrorReport) =>
    request<void>(
      `/api/telemetry/frontend-error`,
      { method: "POST", body: JSON.stringify(payload) },
      true
    ),
});

export { API_BASE_URL };

// ---------------------------------------------------------------------------
// Named exports for methods added via Object.assign above.
// Object.assign mutations are absent from the inferred `typeof api` type, so
// any module that calls these directly must import these named functions
// rather than going through the `api` object.
// ---------------------------------------------------------------------------

/** GET /api/workshops/{id}/members/me → MyRoleResponse */
export function getMyRole(workshopId: number) {
  return request<MyRoleResponse>(`/api/workshops/${workshopId}/members/me`, {}, true);
}

/** GET /api/workshops/{id}/members → MemberPublic[] */
export function listMembers(workshopId: number) {
  return request<MemberPublic[]>(`/api/workshops/${workshopId}/members`, {}, true);
}

/** PATCH /api/workshops/{id}/members/{memberId} → MemberPublic */
export function updateMemberRole(workshopId: number, memberId: number, payload: MemberRoleUpdate) {
  return request<MemberPublic>(
    `/api/workshops/${workshopId}/members/${memberId}`,
    { method: "PATCH", body: JSON.stringify(payload) },
    true
  );
}

/** DELETE /api/workshops/{id}/members/{memberId} */
export function removeMember(workshopId: number, memberId: number) {
  return request<void>(
    `/api/workshops/${workshopId}/members/${memberId}`,
    { method: "DELETE" },
    true
  );
}

/** POST /api/workshops/{id}/members/invite → InvitationCreated */
export function createInvitation(workshopId: number, payload: InvitationCreate) {
  return request<InvitationCreated>(
    `/api/workshops/${workshopId}/members/invite`,
    { method: "POST", body: JSON.stringify(payload) },
    true
  );
}

/** GET /api/workshops/{id}/invitations → InvitationPublic[] */
export function listInvitations(workshopId: number) {
  return request<InvitationPublic[]>(
    `/api/workshops/${workshopId}/invitations`,
    {},
    true
  );
}

/** DELETE /api/workshops/{id}/invitations/{invitationId} */
export function revokeInvitation(workshopId: number, invitationId: number) {
  return request<void>(
    `/api/workshops/${workshopId}/invitations/${invitationId}`,
    { method: "DELETE" },
    true
  );
}

/** POST /api/invitations/accept → MemberPublic */
export function acceptInvitation(payload: InvitationAccept) {
  return request<MemberPublic>(
    `/api/invitations/accept`,
    { method: "POST", body: JSON.stringify(payload) },
    true
  );
}

/** GET /api/invitations/preview?token=... → InvitationPreview (no auth required) */
export function previewInvitation(token: string) {
  return request<InvitationPreview>(
    `/api/invitations/preview?token=${encodeURIComponent(token)}`,
    { method: "GET" },
    false
  );
}

/** POST /api/feedback → FeedbackPublic */
export function submitFeedback(payload: FeedbackCreate) {
  return request<FeedbackPublic>(
    `/api/feedback`,
    { method: "POST", body: JSON.stringify(payload) },
    true
  );
}

/** GET /api/admin/pilot → PilotAdminResponse */
export function getPilotAdmin() {
  return request<PilotAdminResponse>(`/api/admin/pilot`, {}, true);
}

/** GET /api/admin/feedback → AdminFeedbackResponse (paginated)
 * Shape: { total, limit, offset, entries: FeedbackPublic[] }
 */
export function listAdminFeedback() {
  return request<AdminFeedbackResponse>(`/api/admin/feedback`, {}, true);
}

/** PATCH /api/admin/workshops/{id}/toggle-disabled → WorkshopToggleResponse */
export function toggleWorkshopDisabled(workshopId: number) {
  return request<WorkshopToggleResponse>(
    `/api/admin/workshops/${workshopId}/toggle-disabled`,
    { method: "PATCH" },
    true
  );
}

/** PATCH /api/admin/feedback/{id} → FeedbackPublic */
export function updateFeedbackStatus(feedbackId: number, newStatus: string) {
  return request<FeedbackPublic>(
    `/api/admin/feedback/${feedbackId}`,
    { method: "PATCH", body: JSON.stringify({ status: newStatus }) },
    true
  );
}

/** GET /version → VersionResponse (WM-098) */
export function getVersion() {
  return request<VersionResponse>("/version");
}

/** GET /status → StatusResponse (WM-101) */
export function getStatus() {
  return request<StatusResponse>("/status");
}
